<template>
  <div class="h-screen bg-gray-50 flex flex-col">
    <!-- 筛选和搜索区域 -->
    <div class="flex-shrink-0 bg-white p-2 mb-1 shadow-sm">
      <!-- 统计卡片 -->
      <div class="flex items-center justify-between mb-2">
        <div class="grid grid-cols-3 gap-2 flex-1">
          <div class="text-center">
            <p class="text-lg font-bold text-green-600">¥{{ stats.totalAmount.toFixed(2) }}</p>
            <p class="text-xs text-gray-500">进货金额</p>
          </div>
          <div class="text-center">
            <p class="text-lg font-bold text-blue-600">{{ stats.orderCount }}</p>
            <p class="text-xs text-gray-500">进货量</p>
          </div>
          <button
            type="button"
            @click="handleUnauditedStatClick"
            :class="[
              'text-center rounded-lg py-1 transition-colors active:scale-95',
              searchParams.AuditStatus === 0 ? 'bg-orange-50 ring-1 ring-orange-200' : 'hover:bg-orange-50'
            ]"
          >
            <p class="text-lg font-bold text-orange-600">{{ stats.unAuditOrder }}</p>
            <p class="text-xs text-gray-500">未审核</p>
          </button>
        </div>
        <!-- 快捷操作菜单按钮 -->
        <div class="relative ml-2">
          <button @click="showQuickActions = !showQuickActions"
            class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
          <!-- 快捷操作弹出菜单 -->
          <div v-if="showQuickActions" 
            class="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[120px]">
            <button @click="handleQuickAction('/mobile/purchase/add')"
              class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 rounded-t-lg">
              <svg class="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>新增进货</span>
            </button>
            <button @click="handleQuickAction('/mobile/purchase/return-list')"
              class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 border-t border-gray-100">
              <svg class="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3" />
              </svg>
              <span>进货退货</span>
            </button>
            <button @click="handleQuickAction('/mobile/purchase/suppliers')"
              class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 rounded-b-lg">
              <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span>供应商管理</span>
            </button>
          </div>
        </div>
      </div>

      <div class="flex items-center space-x-3">
        <div class="flex-1">
          <input v-model="searchKeyword" type="text" placeholder="搜索进货单号、供应商、商品名称..."
            class="w-full px-3 py-2 border-0 bg-gray-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:bg-white focus:outline-none"
            @input="debounceSearch" />
        </div>
        <button @click="handleSearch" :disabled="searching"
          class="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50">
          <svg v-if="searching" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
        <button @click="showFilter = !showFilter"
          class="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
        </button>
      </div>

      <!-- 筛选选项 -->
      <div v-if="showFilter" class="border-gray-200 pt-3 space-y-3">
        <div class="grid grid-cols-2 gap-3">
          <select v-model="searchParams.AuditStatus" class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            @change="handleSearch">
            <option :value="null">全部审核</option>
            <option :value="0">未审核</option>
            <option :value="1">已审核</option>
          </select>

          <select v-model="searchParams.PaymentStatus" class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            @change="handleSearch">
            <option :value="null">付款状态</option>
            <option :value="0">未付款</option>
            <option :value="1">部分付款</option>
            <option :value="2">已付款</option>
            <option :value="3">已核销</option>
            <option :value="4">部分核销</option>
            <option :value="6">待核销</option>
          </select>
        </div>

        <!-- <div class="grid grid-cols-2 gap-3">
            <select 
              v-model="searchParams.InvoiceStatus"
              class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              @change="handleSearch"
            >
              <option :value="null">发票状态</option>
              <option :value="0">未开票</option>
              <option :value="1">部分开票</option>
              <option :value="2">已开票</option>
            </select>
            <div></div>
          </div> -->
        <div class="grid grid-cols-2 gap-3">
          <input v-model="searchParams.StartDate" type="date"
            class="px-3 py-2 border border-gray-300 rounded-lg text-sm" @change="handleSearch" />
          <input v-model="searchParams.EndDate" type="date" class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            @change="handleSearch" />
        </div>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="flex-1 overflow-y-auto px-1 pb-8">

      <!-- 进货单列表 -->
      <div v-if="loading" class="text-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        <p class="text-gray-500 mt-2">加载中...</p>
      </div>

      <div v-else class="space-y-1 pb-20">
        <div v-for="purchase in tableData" :key="purchase.id"
          class="bg-white rounded-lg p-2 mx-1 shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative"
          @click="viewPurchaseDetail(purchase)">
          
          <!-- 选中遮罩/边框 -->
          <div v-if="selectedRows.includes(purchase.id)" class="absolute inset-0 border-2 border-green-500 rounded-lg pointer-events-none z-10"></div>

          <div class="flex items-start">
             <!-- 选择框 -->
            <div class="mr-2 pt-1" @click.stop>
              <input type="checkbox" 
                     :checked="selectedRows.includes(purchase.id)"
                     @change="toggleRowSelection(purchase.id)"
                     class="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500">
            </div>

            <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between mb-1">
                    <div>
                    <h4 class="font-semibold text-gray-900 text-base leading-tight">{{ purchase.orderNo }}</h4>
                    <p class="text-xs text-gray-500 mt-0.5">{{ purchase.supplierName }}</p>
                    </div>
                </div>

                <div class="flex items-center flex-wrap gap-1 mb-2">
                    <span :class="getAuditStatusClass(purchase.auditStatus)">
                        {{ purchase.auditStatus }}
                    </span>
                    <span :class="getPaymentStatusClass(purchase.paymentStatus)">
                        {{ purchase.paymentStatus }}
                    </span>
                    <span :class="getInvoiceStatusClass(purchase.invoiceStatus)">
                        {{ purchase.invoiceStatus }}
                    </span>
                </div>

                <div class="grid grid-cols-2 gap-2 text-xs mb-2">
                    <div class="flex items-center space-x-2">
                    <span class="text-gray-500">进货金额：</span>
                    <span class="font-semibold text-gray-900 text-sm">¥{{ purchase.totalAmount?.toLocaleString() || '0' }}</span>
                    </div>
                    <div class="flex items-center space-x-2">
                    <span class="text-gray-500">进货日期：</span>
                    <span class="font-semibold text-gray-900 text-sm">{{ formatDate(purchase.orderDate) }}</span>
                    </div>
                </div>
                <!-- 商品信息 -->
                <div class="mb-2">
                    <p class="text-xs text-gray-500 mb-0.5">商品明细:</p>
                    <p class="text-xs text-gray-700 line-clamp-2" :title="purchase.productDetails">{{ purchase.productDetails }}
                    </p>
                </div>
                <div class="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div class="text-xs text-gray-500">
                    <p>制单人：{{ purchase.creatorName }}</p>
                    </div>
                    <div class="flex items-center space-x-1">
                    <button @click.stop="handleSetTop(purchase)"
                        :class="[
                          'p-1 text-base transition-colors',
                          purchase.toptag === 1 ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'
                        ]"
                        :title="purchase.toptag === 1 ? '取消置顶' : '置顶'">
                        <i :class="purchase.toptag === 1 ? 'fas fa-star' : 'far fa-star'"></i>
                    </button>
                    <button @click.stop="handlePrint(purchase, $event)"
                        class="p-1 text-base text-gray-400 hover:text-blue-500 transition-colors"
                        title="打印">
                        <i class="fas fa-print"></i>
                    </button>
                    <button v-if="purchase.auditStatus === '未审核' && hasAuditPermission" @click.stop="handleAudit(purchase)"
                        class="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors">
                        审核
                    </button>
                    <button v-if="purchase.auditStatus === '未审核'" @click.stop="handleDelete(purchase)"
                        class="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors">
                        删除
                    </button>
                    <button
                        v-if="purchase.auditStatus === '已审核' && (purchase.paymentStatus === '未付款' || purchase.paymentStatus === '部分付款')"
                        @click.stop="handleUnAudit(purchase)"
                        class="px-2 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600 transition-colors">
                        反审核
                    </button>
                    <button
                        v-if="purchase.auditStatus === '已审核' && (purchase.paymentStatus === '未付款' || purchase.paymentStatus === '部分付款')"
                        @click.stop="handlePayment(purchase)"
                        class="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors">
                        付款
                    </button>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- 批量操作栏 -->
    <div v-if="selectedRows.length > 0" 
         class="fixed bottom-16 left-0 right-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.1)] z-30 px-4 py-3 flex items-center justify-between border-t border-gray-100">
      <div class="flex items-center space-x-2">
        <label class="flex items-center space-x-2 text-sm text-gray-600">
          <input type="checkbox" 
                 :checked="isAllSelected"
                 @change="handleSelectAll"
                 class="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500">
          <span>全选 ({{ selectedRows.length }})</span>
        </label>
      </div>
      <div class="flex items-center space-x-3">
        <button @click="handleBatchPayment"
          :disabled="!canBatchPayment"
          :class="canBatchPayment ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' : 'bg-gray-300 cursor-not-allowed'"
          class="px-4 py-2 text-white text-sm font-medium rounded-lg shadow-sm transition-colors">
          批量付款
        </button>
      </div>
    </div>

    <!-- 移动端分页组件 - 固定在底部 -->
    <div v-if="!loading && tableData.length > 0 && selectedRows.length === 0" class="fixed bottom-16 left-0 right-0 bg-white border-gray-200 shadow-lg z-40 px-2 py-1">
      <MobilePager 
        :current-page="pagination.current"
        :total-pages="Math.ceil(totalRecords / pagination.pageSize)"
        :page-size="pagination.pageSize"
        :total="totalRecords"
        :show-size-changer="false"
        :show-quick-jumper="false"
        @update:current-page="handlePageChange"
      />
    </div>

    <!-- 下拉刷新指示器 -->
    <!-- <div v-if="refreshing"
      class="fixed top-16 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg z-40">
      <div class="flex items-center space-x-2">
        <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        <span class="text-sm">刷新中...</span>
      </div>
    </div> -->
    
    <!-- 审核确认对话框 -->
    <ConfirmDialog
      :visible="auditConfirmDialogVisible"
      type="audit"
      title="审核确认"
      :message="`确定要审核进货单吗？`"
      :item-name="pendingAuditItem?.orderNo"
      :loading="auditLoading"
      @confirm="confirmAuditAction"
      @cancel="auditConfirmDialogVisible = false"
    />

    <!-- 反审核确认对话框 -->
    <ConfirmDialog
      :visible="unauditConfirmDialogVisible"
      type="unaudit"
      title="反审核确认"
      :message="`确定要反审核进货单吗？`"
      :item-name="pendingUnAuditItem?.orderNo"
      :loading="unauditLoading"
      @confirm="confirmUnAuditAction"
      @cancel="unauditConfirmDialogVisible = false"
    />

    <!-- 删除确认对话框 -->
    <ConfirmDialog
      :visible="deleteConfirmDialogVisible"
      type="delete"
      title="删除确认"
      message="确定要删除该进货单吗？"
      :item-name="pendingDeleteItem?.orderNo"
      warning-text="删除后将无法恢复，请谨慎操作！"
      :loading="deleteLoading"
      @confirm="confirmDeleteAction"
      @cancel="deleteConfirmDialogVisible = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import dayjs from 'dayjs'
import { useRouter } from 'vue-router'
import { purchaseApi } from '@/api/api'
import { toast } from '@/utils/toast.js'
import MobilePager from '@/mobile/components/MobilePager.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

const router = useRouter()

const loading = ref(false)
const refreshing = ref(false)
const showFilter = ref(false)
const hasAuditPermission = ref(true)
const searching = ref(false)
const totalRecords = ref(0)
const showQuickActions = ref(false)
const selectedRows = ref([])

const isAllSelected = computed(() => {
  return tableData.value.length > 0 && selectedRows.value.length === tableData.value.length
})

const handleSelectAll = () => {
  if (isAllSelected.value) {
    selectedRows.value = []
  } else {
    selectedRows.value = tableData.value.map(item => item.id)
  }
}

const toggleRowSelection = (id) => {
  const index = selectedRows.value.indexOf(id)
  if (index === -1) {
    selectedRows.value.push(id)
  } else {
    selectedRows.value.splice(index, 1)
  }
}

// 批量操作权限判断
const canBatchPayment = computed(() => {
  if (selectedRows.value.length === 0) return false
  const selectedItems = tableData.value.filter(item => selectedRows.value.includes(item.id))
  return selectedItems.every(item => 
    item.auditStatus === '已审核' && 
    ['未付款', '部分付款'].includes(item.paymentStatus)
  )
})

const handlePrint = (purchase, event) => {
  event?.stopPropagation()
  router.push({
    name: 'PurchasePrint',
    params: { id: purchase.id }
  })
}

const handleSetTop = async (purchase) => {
  try {
    const isTop = purchase.toptag !== 1
    const res = await purchaseApi.setTop({
      OrderId: parseInt(purchase.id),
      IsTop: isTop
    })

    if (res.success) {
      purchase.toptag = isTop ? 1 : 0
      toast.success(isTop ? '置顶成功' : '取消置顶成功')
      await fetchData()
    } else {
      toast.error(res.message || (isTop ? '置顶失败' : '取消置顶失败'))
    }
  } catch (error) {
    console.error('置顶操作失败:', error)
    toast.error('置顶操作失败')
  }
}

const handleBatchPayment = () => {
  if (selectedRows.value.length === 0) {
    toast.warning('请先选择需要付款的进货单据')
    return
  }

  const selectedItems = tableData.value.filter(item => selectedRows.value.includes(item.id))
  const validItems = selectedItems.filter(item =>
    item.auditStatus === '已审核' &&
    ['未付款', '部分付款', '部分核销'].includes(item.paymentStatus)
  )

  if (validItems.length === 0) {
    toast.warning('所选单据中没有已审核且有未付金额的单据，请重新选择')
    return
  }

  const supplierNames = [...new Set(validItems.map(item => item.supplierName))]
  if (supplierNames.length > 1) {
    toast.warning('批量付款只能选择同一供应商的单据，请重新选择')
    return
  }

  const supplierId = validItems[0].supplierId
  const supplierName = validItems[0].supplierName
  const orderIds = validItems.map(item => item.id).join(',')

  router.push({
    path: '/mobile/finance/payment/add',
    query: {
      supplierId,
      supplierName,
      orderIds
    }
  })
}

const stats = ref({
  todayCount: 0,
  totalAmount: 0,
  pendingCount: 0,
  orderCount: 0,
  unAuditOrder: 0,
})

const searchParams = ref({
  ProductName: '',
  OrderNo: '',
  SupplierName: '',
  CreatorName: '',
  PaymentStatus: null,
  AuditStatus: null,
  InvoiceStatus: null,
  StartDate: '',
  EndDate: '',
  keyword: ''
})

const pagination = ref({
  current: 1,
  pageSize: 20
})

const tableData = ref([])

// 计算内容区域顶部填充
// const contentPaddingTop = computed(() => {
//   const baseHeight = showFilter.value ? '200px' : '120px'
//   return baseHeight
// })

// 状态映射
const paymentStatusMap = {
  0: '未付款',
  1: '部分付款',
  2: '已付款',
  3: '已核销',
  4: '部分核销',
  6: '待核销'
}

const auditStatusMap = {
  0: '未审核',
  1: '已审核'
}

const invoiceStatusMap = {
  0: '未开票',
  1: '部分开票',
  2: '已开票'
}

const getAuditStatusClass = (status) => {
  const baseClass = 'px-2 py-0.5 rounded-full text-xs font-medium'
  const classes = {
    '未审核': 'bg-red-100 text-red-800',
    '已审核': 'bg-green-100 text-green-800'
  }
  return `${baseClass} ${classes[status] || 'bg-gray-100 text-gray-800'}`
}

const getPaymentStatusClass = (status) => {
  const baseClass = 'px-2 py-0.5 rounded-full text-xs font-medium'
  const classes = {
    '未付款': 'bg-red-100 text-red-700',
    '部分付款': 'bg-orange-100 text-orange-800',
    '已付款': 'bg-green-100 text-green-800',
    '已核销': 'bg-blue-100 text-blue-800',
    '部分核销': 'bg-purple-100 text-purple-800',
    '待核销': 'bg-indigo-100 text-indigo-800'
  }
  return `${baseClass} ${classes[status] || 'bg-gray-100 text-gray-800'}`
}

const getInvoiceStatusClass = (status) => {
  const baseClass = 'px-2 py-0.5 rounded-full text-xs font-medium'
  const classes = {
    '未开票': 'bg-red-100 text-red-700',
    '部分开票': 'bg-orange-100 text-orange-800',
    '已开票': 'bg-green-100 text-green-800'
  }
  return `${baseClass} ${classes[status] || 'bg-blue-100 text-blue-700'}`
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return dayjs(dateStr).format('YYYY/MM/DD')
}

// 搜索防抖
let searchTimer = null
const debounceSearch = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    handleSearch()
  }, 500)
}

const handleSearch = async () => {
  pagination.value.current = 1
  searching.value = true
  selectedRows.value = []
  await fetchData()
  searching.value = false
}

const handleUnauditedStatClick = async () => {
  searchParams.value.AuditStatus = searchParams.value.AuditStatus === 0 ? null : 0
  showFilter.value = true
  await handleSearch()
}
   
// 获取数据
const fetchData = async () => {
  try {
    loading.value = true

    const keyword = (searchParams.value.keyword || '').trim()
    
    // 智能处理开始日期逻辑
    const getSmartStartDate = () => {
      // 如果用户明确设置了开始日期，使用用户设置的日期
      if (searchParams.value.StartDate) {
        return searchParams.value.StartDate
      }
      
      // 检查是否有其他查询条件
      const hasOtherConditions = keyword || 
        searchParams.value.ProductName || 
        searchParams.value.OrderNo || 
        searchParams.value.SupplierName || 
        searchParams.value.CreatorName || 
        searchParams.value.PaymentStatus !== null || 
        searchParams.value.AuditStatus !== null || 
        searchParams.value.InvoiceStatus !== null || 
        searchParams.value.EndDate
      
      // 如果没有任何查询条件，默认查询本月数据
      if (!hasOtherConditions) {
        const currentMonth = new Date()
        return currentMonth.getFullYear() + '-' + String(currentMonth.getMonth() + 1).padStart(2, '0') + '-01'
      }
      
      // 如果有其他查询条件但未指定开始日期，返回null（不限制开始日期）
      return null
    }
    
    const searchData = {
      ProductName: searchParams.value.ProductName || keyword || '',
      CreatorName: searchParams.value.CreatorName || '',
      PaymentStatus: searchParams.value.PaymentStatus,
      AuditStatus: searchParams.value.AuditStatus,
      InvoiceStatus: searchParams.value.InvoiceStatus,
      StartDate: getSmartStartDate(),
      EndDate: searchParams.value.EndDate || null
    }

    const res = await purchaseApi.getPurchaseList({
      pagination: {
        current: pagination.value.current,
        pageSize: pagination.value.pageSize
      },
      search: searchData,
      sort: { predicate: "orderDate", reverse: true }
    })

    if (res.success) {
      const mappedData = res.data.list.map(item => ({
        ...item,
        paymentStatus: paymentStatusMap[item.paymentStatus] || '未知',
        invoiceStatus: invoiceStatusMap[item.invoiceStatus] || '未开票',
        auditStatus: auditStatusMap[item.auditStatus] || '未知'
      }))

      tableData.value = mappedData
      totalRecords.value = res.data.pagination.total || 0

      // 更新统计
      if (res.data.summary) {
        stats.value = {
          todayCount: res.data.summary.todayCount || 0,
          totalAmount: res.data.summary.totalAmount || 0,
          pendingCount: res.data.summary.pendingCount || 0,
          orderCount: res.data.summary.orderCount || 0,
          unAuditOrder: res.data.summary.unAuditOrder || 0,
        }
      }
    }
  } catch (error) {
    console.error('获取进货列表失败:', error)
    if (window.mobileApp) {
      window.mobileApp.showToast('获取数据失败，请重试', 'error')
    }
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

const handlePageChange = async (page) => {
  // 立即设置loading状态并清空当前数据
  loading.value = true
  tableData.value = []
  pagination.value.current = page
  
  // 获取新数据
  await fetchData()
}

const viewPurchaseDetail = (purchase) => {
  router.push(`/mobile/purchase/detail/${purchase.id}`)
}

// 确认对话框相关状态
const auditConfirmDialogVisible = ref(false)
const pendingAuditItem = ref(null)
const auditLoading = ref(false)

const handleAudit = (purchase) => {
  pendingAuditItem.value = purchase
  auditConfirmDialogVisible.value = true
}

const confirmAuditAction = async () => {
  if (!pendingAuditItem.value) return
  
  try {
    auditLoading.value = true
    const purchase = pendingAuditItem.value
    
    if (purchase.auditStatus === '未审核') {
      const res = await purchaseApi.AuditOrder({ OrderId: parseInt(purchase.id) })
      if (res.success) {
        if (window.mobileApp) {
          toast.success(`进货单 ${purchase.orderNo} 审核成功`)
        }
        auditConfirmDialogVisible.value = false
        await fetchData()
      } else {
        if (window.mobileApp) {
          toast.error('审核失败: ' + res.message)
        }
      }
    }
  } catch (error) {
    console.error('审核失败:', error)
    if (window.mobileApp) {
      toast.error('审核失败:' + error)
    }
  } finally {
    auditLoading.value = false
    pendingAuditItem.value = null
  }
}

const handlePayment = (purchase) => {
  if (purchase.auditStatus !== '已审核') {
    toast.warning('只能对已审核的订单进行付款')
    return
  }
  const unPayAmount = parseFloat(purchase.unPayAmount || purchase.unpaidAmount || 0)
  if (unPayAmount <= 0) {
    toast.warning('该订单没有未付金额')
    return
  }
  router.push({
    path: '/mobile/finance/payment/add',
    query: {
      supplierId: purchase.supplierId,
      supplierName: purchase.supplierName,
      orderIds: purchase.id
    }
  })
}

const unauditConfirmDialogVisible = ref(false)
const pendingUnAuditItem = ref(null)
const unauditLoading = ref(false)

const handleUnAudit = (purchase) => {
  pendingUnAuditItem.value = purchase
  unauditConfirmDialogVisible.value = true
}

const confirmUnAuditAction = async () => {
  if (!pendingUnAuditItem.value) return
  
  try {
    unauditLoading.value = true
    const purchase = pendingUnAuditItem.value
    
    const res = await purchaseApi.UnAuditOrder({ OrderId: parseInt(purchase.id) })
    if (res.success) {
      toast.success(`进货单 ${purchase.orderNo} 反审核成功`)
      unauditConfirmDialogVisible.value = false
      await fetchData()
    } else {
      toast.error('反审核失败: ' + res.message)
    }
  } catch (error) {
    console.error('反审核失败:', error)
    toast.error('反审核失败:' + error)
  } finally {
    unauditLoading.value = false
    pendingUnAuditItem.value = null
  }
}

const deleteConfirmDialogVisible = ref(false)
const pendingDeleteItem = ref(null)
const deleteLoading = ref(false)

const handleDelete = (purchase) => {
  pendingDeleteItem.value = purchase
  deleteConfirmDialogVisible.value = true
}

const confirmDeleteAction = async () => {
  if (!pendingDeleteItem.value) return
  
  try {
    deleteLoading.value = true
    const purchase = pendingDeleteItem.value
    
    const res = await purchaseApi.deletePurchase(parseInt(purchase.id))
    if (res.success) {
      toast.success(`进货单 ${purchase.orderNo} 删除成功`)
      deleteConfirmDialogVisible.value = false
      await fetchData()
    } else {
      toast.error('删除失败: ' + res.message)
    }
  } catch (error) {
    console.error('删除失败:', error)
    toast.error('删除失败:' + error)
  } finally {
    deleteLoading.value = false
    pendingDeleteItem.value = null
  }
}

// 快捷操作处理
const handleQuickAction = (path) => {
  showQuickActions.value = false
  router.push(path)
}

// 下拉刷新
let startY = 0
let currentY = 0
let isRefreshing = false

const handleTouchStart = (e) => {
  startY = e.touches[0].clientY
}

const handleTouchMove = (e) => {
  if (isRefreshing) return

  currentY = e.touches[0].clientY
  const deltaY = currentY - startY

  if (deltaY > 50 && window.scrollY === 0) {
    e.preventDefault()
  }
}

const handleTouchEnd = () => {
  if (isRefreshing) return

  const deltaY = currentY - startY
  if (deltaY > 50 && window.scrollY === 0) {
    isRefreshing = true
    refreshing.value = true
    pagination.value.current = 1
    fetchData().finally(() => {
      isRefreshing = false
    })
  }
}

onMounted(() => {
  // 页面初始化时StartDate设为空字符串，但默认查询本月数据
  searchParams.value.StartDate = ''
  fetchData()
  document.addEventListener('touchstart', handleTouchStart, { passive: false })
  document.addEventListener('touchmove', handleTouchMove, { passive: false })
  document.addEventListener('touchend', handleTouchEnd, { passive: false })
})

onUnmounted(() => {
  document.removeEventListener('touchstart', handleTouchStart)
  document.removeEventListener('touchmove', handleTouchMove)
  document.removeEventListener('touchend', handleTouchEnd)
  if (searchTimer) {
    clearTimeout(searchTimer)
  }
})
</script>

<style scoped>
/* 确保卡片悬停效果 */
.hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* 状态标签样式 */
.rounded-full {
  border-radius: 9999px;
}
</style>
